import React from 'react';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { FiTarget, FiBookOpen, FiAward, FiArrowRight } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import reforcoIcon from '@/assets/images/infantil/trilha-reforco.png';
import mapaIcon from '@/assets/images/infantil/trilha-mapa.png';
import enemIcon from '@/assets/images/infantil/trilha-enem.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Ícone (variante original) + ilustração flat estilo home (variante lúdica) +
// descrição padrão por tipo de trilha.
const KIND_META = {
  adaptive: { Icon: FiTarget, illustration: reforcoIcon, fallbackDesc: 'Foca no que você mais erra, em todas as matérias. Sempre disponível.' },
  exam: { Icon: FiBookOpen, illustration: mapaIcon, fallbackDesc: 'Alinhada à matriz do exame, priorizando as suas lacunas dentro dele.' },
  enem: { Icon: FiAward, illustration: enemIcon, fallbackDesc: 'Em breve.' }
};

// ===================== Variante LÚDICA (Layout B) =====================

// Botão CTA com aparência 3D/clicável (gradiente + highlight interno + sombra embaixo).
function TrilhaButton({ isEnem, isActive, starting, onContinue, onStart, pathId }) {
  if (isEnem) {
    return (
      <button
        type="button"
        disabled
        className="w-full cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 py-3 text-sm font-bold text-neutral-400"
      >
        Em breve
      </button>
    );
  }

  if (isActive) {
    return (
      <button
        type="button"
        onClick={onContinue}
        className="group/btn relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{
          background: 'var(--gradient-btn-continue)',
          boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)'
        }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
        />
        <span className="relative flex items-center justify-center gap-2">
          {/* Círculo branco com ícone play */}
          <span
            aria-hidden="true"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/30"
          >
            <span className="text-[10px] leading-none">▶</span>
          </span>
          Continuar
          <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={starting}
      onClick={() => onStart(pathId)}
      className="group/btn relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97] disabled:opacity-60"
      style={{
        background: 'var(--gradient-btn-start)',
        boxShadow: '0 4px 0 var(--color-secondary-dark), 0 6px 12px rgba(68,43,140,0.35)'
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)' }}
      />
      <span className="relative">Começar trilha</span>
    </button>
  );
}

function PlayfulTrilhaCard({ path, index = 0, starting, onStart, onContinue, missionLabel }) {
  const meta = KIND_META[path.kind] ?? KIND_META.adaptive;
  const isActive = path['user-status'] === 'active';
  const isEnem = path.kind === 'enem';

  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} ${index * 70}ms both` }}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[24px] border bg-[var(--color-bg-cream)] p-6',
        'shadow-card transition-[box-shadow,transform,border-color] duration-150 ease-out-strong',
        !isEnem && 'hover:-translate-y-0.5 hover:shadow-card-hover',
        isActive ? 'border-[var(--color-mission-medium)]/50' : 'border-neutral-100',
        isEnem && 'opacity-70'
      )}
    >
      {/* Faixa decorativa de céu suave no topo */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-[24px]"
        style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.45) 0%, transparent 100%)' }}
      />

      {/* Arbustos decorativos nos cantos inferiores */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-2 left-2 text-2xl opacity-30"
      >
        🌿
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-2 right-2 text-2xl opacity-30"
      >
        🌿
      </span>

      {/* Topo do card: ilustração flat (mesmo estilo dos nav-cards da home) + badge */}
      <div className="relative mb-5 flex items-start justify-between">
        <img
          src={meta.illustration}
          alt=""
          aria-hidden="true"
          className="h-16 w-16 object-contain drop-shadow-sm"
        />

        {isActive && (
          <div className="flex items-center gap-1">
            <span className="rounded-full bg-[var(--color-bg-mission-soft)] px-2.5 py-1 text-xs font-bold text-[var(--color-mission-medium)]">Em andamento</span>
            <span
              aria-hidden="true"
              className="text-sm"
            >
              ⭐
            </span>
          </div>
        )}
      </div>

      <h3
        className="text-lg font-bold"
        style={{ color: '#222731' }}
      >
        {path.name}
      </h3>
      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-neutral-500">{path.description || meta.fallbackDesc}</p>

      {/* Missão do dia — só renderiza se missionLabel for passado */}
      {missionLabel && (
        <div className="mb-4 mt-3 flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-400">Missão de hoje:</span>
          <span
            className="rounded-full px-2.5 py-0.5 text-xs font-extrabold"
            style={{ background: 'var(--color-bg-mission-cream)', color: 'var(--color-mission-text)' }}
          >
            {missionLabel}
          </span>
        </div>
      )}

      <div className={missionLabel ? '' : 'mt-6'}>
        <TrilhaButton
          isEnem={isEnem}
          isActive={isActive}
          starting={starting}
          onContinue={onContinue}
          onStart={onStart}
          pathId={path.id}
        />
      </div>
    </article>
  );
}

// ===================== Variante ORIGINAL (Layout A / default) =====================

const ORIGINAL_ICON_WRAP = {
  adaptive: 'bg-mission-light text-mission',
  exam: 'bg-info-light text-info',
  enem: 'bg-neutral-100 text-neutral-400'
};

function OriginalTrilhaCard({ path, index = 0, starting, onStart, onContinue }) {
  const meta = KIND_META[path.kind] ?? KIND_META.adaptive;
  const Icon = meta.Icon;
  const isActive = path['user-status'] === 'active';
  const isEnem = path.kind === 'enem';
  const iconWrap = ORIGINAL_ICON_WRAP[path.kind] ?? ORIGINAL_ICON_WRAP.adaptive;

  return (
    <article
      style={{ animation: `trilha-in 380ms ${EASE_OUT} ${index * 70}ms both` }}
      className={cn(
        'group relative flex flex-col rounded-2xl border bg-white p-6',
        'shadow-card transition-[box-shadow,transform,border-color] duration-150 ease-out-strong',
        !isEnem && 'hover:-translate-y-0.5 hover:shadow-card-hover',
        isActive ? 'border-mission/60' : 'border-neutral-100',
        isEnem && 'opacity-70'
      )}
    >
      {isActive && <span className="absolute right-5 top-5 rounded-full bg-mission-light px-2.5 py-1 text-xs font-bold text-mission">Em andamento</span>}

      <div className={cn('mb-5 flex h-12 w-12 items-center justify-center rounded-2xl', iconWrap)}>
        <Icon
          className="h-6 w-6"
          strokeWidth={2.2}
        />
      </div>

      <h3 className="text-lg font-bold text-neutral-900">{path.name}</h3>
      <p className="mb-6 mt-1.5 flex-1 text-sm leading-relaxed text-neutral-500">{path.description || meta.fallbackDesc}</p>

      {isEnem ? (
        <Button
          type="button"
          variant="outline"
          fullWidth
          disabled
        >
          Em breve
        </Button>
      ) : isActive ? (
        <Button
          type="button"
          variant="mission"
          fullWidth
          onClick={onContinue}
        >
          Continuar
          <FiArrowRight className="ml-1.5 h-4 w-4 transition-transform duration-150 ease-out-strong group-hover:translate-x-0.5" />
        </Button>
      ) : (
        <Button
          type="button"
          variant="primary"
          fullWidth
          disabled={starting}
          onClick={() => onStart(path.id)}
        >
          Começar trilha
        </Button>
      )}
    </article>
  );
}

// Dispatcher: tenant playful → card lúdico; senão → card original. Gateado pela env var global.
export function TrilhaCard(props) {
  const playfulStudent = usePlayfulStudent();
  return playfulStudent ? <PlayfulTrilhaCard {...props} /> : <OriginalTrilhaCard {...props} />;
}
